import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal.js';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Select, type SelectOption } from '../ui/Select.js';
import { useUpdateProject, type ProjectRow } from '../../hooks/useProjects.js';

interface ProjectInfoEditModalProps {
  project: ProjectRow;
  open: boolean;
  onClose: () => void;
}

export const LANGUAGE_OPTIONS: SelectOption<string>[] = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
];

const TYPE_OPTIONS: SelectOption<string>[] = [
  { value: 'website', label: 'Website' },
  { value: 'web_audit', label: 'Web Audit' },
];

const SUBTYPE_OPTIONS: SelectOption<string>[] = [
  { value: 'single_page', label: 'Single Page' },
];

const FIELD_LABEL: React.CSSProperties = {
  display: 'block',
  marginBottom: '4px',
  fontSize: 'var(--evo-fs-caption)',
  fontWeight: 'var(--evo-fw-medium)',
  color: 'var(--evo-fg-3)',
  fontFamily: 'var(--evo-font)',
};

export function ProjectInfoEditModal({ project, open, onClose }: ProjectInfoEditModalProps) {
  const { t } = useTranslation();
  const updateProject = useUpdateProject();

  const [type, setType] = useState(project.type);
  const [subtype, setSubtype] = useState<string | null>(project.subtype ?? null);
  const [language, setLanguage] = useState(project.language);
  const [deadline, setDeadline] = useState(project.desiredDeadline?.slice(0, 10) ?? '');

  useEffect(() => {
    if (!open) return;
    setType(project.type);
    setSubtype(project.subtype ?? null);
    setLanguage(project.language);
    setDeadline(project.desiredDeadline?.slice(0, 10) ?? '');
  }, [project.id, project.type, project.subtype, project.language, project.desiredDeadline, open]);

  function handleSave() {
    updateProject.mutate(
      { id: project.id, type, subtype: subtype || null, language, desiredDeadline: deadline || null },
      { onSuccess: onClose },
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('projects.detail.editModal.title')}
      size="sm"
      footer={
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Button variant="ghost" tone="muted" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="solid" tone="cyan" size="sm" loading={updateProject.isPending} onClick={handleSave}>
            {t('common.save')}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
        <div>
          <label style={FIELD_LABEL}>{t('projects.columns.type')}</label>
          <Select
            value={type}
            onChange={v => { if (v) setType(v); }}
            options={TYPE_OPTIONS}
            fullWidth
          />
        </div>
        <div>
          <label style={FIELD_LABEL}>{t('projects.columns.subtype')}</label>
          <Select
            value={subtype}
            onChange={v => setSubtype(v)}
            options={SUBTYPE_OPTIONS}
            placeholder="—"
            clearable
            fullWidth
          />
        </div>
        <div>
          <label style={FIELD_LABEL}>{t('projects.fields.language')}</label>
          <Select
            value={language}
            onChange={v => { if (v) setLanguage(v); }}
            options={LANGUAGE_OPTIONS}
            fullWidth
          />
        </div>
        <div>
          <label style={FIELD_LABEL}>{t('projects.columns.deadline')}</label>
          <Input
            type="date"
            value={deadline}
            onChange={v => setDeadline(v)}
            fullWidth
          />
        </div>
      </div>
    </Modal>
  );
}
